import React, { useState } from 'react'
import PropTypes from 'prop-types'

import cl from 'classnames'
import { Box, Paper, Typography } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'

import { MAX_NUMBER_OF_PAGES, MIN_BOX_BOOK_NUMBER_OF_PAGES } from 'common/utils/bookSize.constants'
import ProductDescriptionItem from 'artkive/components/PageSections/ProductDescriptionItem'
import { TrackButton } from 'artkive/components/Tracker'
import { GET_BOX_CLICK } from 'artkive/constants/tracker/mainNavigation'
import useMobileBreakpoint from 'artkive/hooks/useMobileBreakpoint'
import useSale from 'artkive/hooks/useSale'
import PriceSlider from 'artkive/pages/Books/PriceSlider'
import { getPrice } from 'artkive/pages/Books/ValueLabelComponent'
import { pricingBookBullets } from 'artkive/pages/Pricing/Pricing.constants'

import ArtBooks from 'images/ecom/pricing/ArtBooks.png'
import ArtBooksX2 from 'images/ecom/pricing/ArtBooks-x2.png'
import StorageBadge from 'images/ecom/StorageBadge.svg'

import ImageTextSection, { Picture } from '../ImageTextSection'
import { boxProductDisclaimers } from '../Pricing.constants'
import { CaptionBox } from '../Pricing.style'

import { ImageBox, StorageBadgeImage } from './BooksSection.style'

const useStyles = makeStyles(({ breakpoints }) => ({
  costHint: {
    '&&': {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    height: 74,
    border: '2px solid #E6F1FC',
    borderRadius: 8,
    padding: '0 8px',
    textAlign: 'center',
    [breakpoints.up('md')]: {
      width: 120,
      textAlign: 'right',
    },
  },
  price: {
    fontSize: '1.5rem',
    lineHeight: 1.5,
  },
  sliderRail: {
    left: -16,
    right: -16,
    width: 'calc(100% + 32px)',
  },
  sliderTrack: {
    transform: 'translateX(-16px)',
  },
}))

const BooksSection = ({ pricing }) => {
  const classes = useStyles()
  const muiTheme = useTheme()

  const [sliderValue, setSliderValue] = useState(MIN_BOX_BOOK_NUMBER_OF_PAGES)

  const handleSliderChange = (e, value) => setSliderValue(value)

  const isMobile = useMobileBreakpoint()
  const { boxCheckoutUrl } = useSale()

  return (
    <ImageTextSection
      reverse
      bgColor='#D3EFFB'
      imageContent={
        <ImageBox>
          <StorageBadgeImage src={StorageBadge} alt='+ 3 Months Storage' />
          <Picture>
            <source srcSet={`${ArtBooksX2}`} media={muiTheme.breakpoints.up('lg').replace('@media ', '')} />
            <source
              srcSet={`${ArtBooks}, ${ArtBooksX2} 2x`}
              media={muiTheme.breakpoints.up('md').replace('@media ', '')}
            />
            <img srcSet={`${ArtBooks}, ${ArtBooksX2} 2x`} src={ArtBooks} alt='Kids Art Books preview' />
          </Picture>
        </ImageBox>
      }
      descriptionContent={
        <div>
          <Typography variant='h2' align={isMobile ? 'center' : 'left'}>
            Kids Art Books
          </Typography>
          <Box mt={3} mb={4}>
            {pricingBookBullets.map(({ text }, index) => (
              <ProductDescriptionItem key={index}>{text}</ProductDescriptionItem>
            ))}
          </Box>
          <Paper elevation={0} className={cl(classes.costHint)}>
            <Typography component='span' className='test-pricing-label'>
              {sliderValue} Pieces
            </Typography>
            <Typography component='span' style={{ padding: '0 12px' }}>
              —
            </Typography>
            <Typography variant='h6' className={cl(classes.price, 'test-pricing-amount')}>
              {getPrice(sliderValue, pricing)}
            </Typography>
          </Paper>
          <Box px={2} my={3} flex={1} width='100%'>
            <PriceSlider
              value={sliderValue}
              onChange={handleSliderChange}
              classes={{ rail: classes.sliderRail, track: classes.sliderTrack }}
            />
          </Box>
          <CaptionBox>
            <Typography variant='caption' component={Box} mb={2}>
              *The pricing slider is per book. If you send more than {MAX_NUMBER_OF_PAGES} pieces for one book, we will
              split the pieces into multiple book volumes
            </Typography>
            <Typography variant='caption' component={Box} mb={2}>
              {boxProductDisclaimers[0]}
            </Typography>
            <Typography variant='caption' component={Box}>
              {boxProductDisclaimers[1]}
            </Typography>
          </CaptionBox>
          <Box display='flex' justifyContent={isMobile ? 'center' : 'left'} mt={isMobile ? 5 : 6}>
            <TrackButton
              color='primary'
              href={boxCheckoutUrl}
              size='large'
              variant='contained'
              trackerEvent={GET_BOX_CLICK}
              trackerOptions={{
                section: 'get_books_section',
              }}
            >
              Get My Box
            </TrackButton>
          </Box>
        </div>
      }
    />
  )
}

BooksSection.propTypes = {
  pricing: PropTypes.object.isRequired,
}

export default BooksSection
